import React, { useState, useEffect, useCallback } from 'react';
import { X, PushPin, EnvelopeSimple, CheckCircle, Clock } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

/**
 * MailFollowUpsModal — toont verzonden mails waarop nog geen antwoord is ontvangen.
 *
 * Acties per mail:
 *  - ✓ Dismiss: markeer als afgehandeld (geen reminder meer)
 *  - 🕒 Snooze: stel reminder X dagen uit
 */
export default function MailFollowUpsModal({ onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/mail/follow-ups');
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Kon follow-ups niet laden');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (mid) => {
    try {
      await API.post(`/mail/follow-ups/${mid}/dismiss`);
      toast.success('Follow-up afgehandeld');
      load();
      onChange?.();
    } catch (e) {
      toast.error('Mislukt');
    }
  };

  const snooze = async (mid, dagen) => {
    try {
      const r = await API.post(`/mail/follow-ups/${mid}/snooze`, { dagen });
      toast.success(`Uitgesteld tot ${new Date(r.data.new_due_at).toLocaleDateString('nl-BE')}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error('Mislukt');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="mail-followups-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b bg-amber-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PushPin size={20} weight="duotone" className="text-amber-700" />
            <div>
              <h3 className="text-base font-bold text-gray-900">Follow-up herinneringen</h3>
              <p className="text-xs text-gray-600 mt-0.5">Verzonden mails zonder antwoord — tijd voor een herinnering?</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-amber-100 rounded"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {loading && <div className="text-center text-sm text-gray-500 py-8">Laden...</div>}
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle size={48} className="mx-auto mb-3 opacity-30 text-green-500" />
              <p className="text-sm font-semibold text-gray-700">Alles afgehandeld 🎉</p>
              <p className="text-xs mt-1">Geen openstaande follow-up reminders.</p>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2">
              {items.map(it => {
                const sentDate = new Date(it.datum);
                const dueDate = new Date(it.follow_up_due_at);
                const daysSinceSent = Math.floor((Date.now() - sentDate.getTime()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={it.id} className="border border-amber-200 rounded p-3 hover:bg-amber-50/50" data-testid={`mail-followup-row-${it.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate inline-flex items-center gap-1">
                          <EnvelopeSimple size={14} className="text-gray-400" />
                          {it.subject || '(geen onderwerp)'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">→ {(it.to_emails || []).join(', ')}</div>
                      </div>
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-200 text-amber-900 flex-shrink-0 inline-flex items-center gap-1">
                        <Clock size={10} weight="fill" /> {daysSinceSent}d geen antwoord
                      </span>
                    </div>
                    {it.snippet && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{it.snippet}</p>}
                    <div className="text-[11px] text-gray-500 mt-2">
                      Verzonden: {sentDate.toLocaleDateString('nl-BE')} · Reminder triggerde: {dueDate.toLocaleDateString('nl-BE')}
                    </div>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <button
                        onClick={() => dismiss(it.id)}
                        className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1 font-semibold"
                        data-testid={`mail-followup-dismiss-${it.id}`}
                      >
                        <CheckCircle size={12} weight="fill" /> Afgehandeld
                      </button>
                      <button
                        onClick={() => snooze(it.id, 3)}
                        className="px-2 py-1 text-xs bg-white hover:bg-amber-100 border border-amber-400 text-amber-800 rounded"
                        data-testid={`mail-followup-snooze-3d-${it.id}`}
                      >
                        +3 dagen
                      </button>
                      <button
                        onClick={() => snooze(it.id, 7)}
                        className="px-2 py-1 text-xs bg-white hover:bg-amber-100 border border-amber-400 text-amber-800 rounded"
                        data-testid={`mail-followup-snooze-7d-${it.id}`}
                      >
                        +1 week
                      </button>
                      <button
                        onClick={() => snooze(it.id, 14)}
                        className="px-2 py-1 text-xs bg-white hover:bg-amber-100 border border-amber-400 text-amber-800 rounded"
                        data-testid={`mail-followup-snooze-14d-${it.id}`}
                      >
                        +2 weken
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
